/**
 * Consent Guard Component
 *
 * Wraps protected content and checks if user has accepted required consents.
 * Shows ConsentRequiredPage if consents are missing.
 */

import { LoadingSpinner } from '@xhubio-saas/core-frontend/kernel'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { trpc } from '@/trpc'
import { useConsentCheckQuery } from '../api/consentClient'
import { ConsentRequiredPage } from '../pages/auth/ConsentRequiredPage'
import { OnboardingGuard } from './OnboardingGuard'

interface ConsentGuardProps {
  children: React.ReactNode
}

/**
 * Guard component that checks consent status before rendering children.
 *
 * Uses React Query caching to avoid refetching on every route change.
 * When consents are missing, shows the ConsentRequiredPage.
 */
export function ConsentGuard({ children }: ConsentGuardProps) {
  const { i18n } = useTranslation()
  const { data, isLoading, refetch } = useConsentCheckQuery(i18n.language)
  const utils = trpc.useUtils()

  const handleConsentAccepted = useCallback(() => {
    // Invalidate the consent check query to refetch
    utils.consent.checkRequired.invalidate()
    refetch()
  }, [utils, refetch])

  // Show loading state while checking consent
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Show consent page if any consents are missing
  if (data && !data.allAccepted && data.missing.length > 0) {
    return (
      <ConsentRequiredPage
        missingConsents={data.missing}
        onConsentAccepted={handleConsentAccepted}
      />
    )
  }

  // Render children behind the OnboardingGuard so post-consent users still
  // get the onboarding survey laid over the app until onboardingCompleted=true.
  return <OnboardingGuard>{children}</OnboardingGuard>
}
